<template>
  <div class="review-thumbnail-strip" :class="{ 'is-vertical': layout === 'vertical' }">
    <div class="strip-scroll">
      <slot name="prepend"></slot>
      <template v-for="(group, gIndex) in groups" :key="group.reviewId || gIndex">
        <!-- Group header -->
        <div
          v-if="group.deliverableName"
          class="thumb-group-header"
          @click="toggleGroup(group.reviewId)"
        >
          <div class="group-header-left">
            <span class="icon is-small group-chevron">
              <font-awesome-icon :icon="['fal', collapsedGroups[group.reviewId] ? 'chevron-right' : 'chevron-down']" />
            </span>
            <div class="group-label">
              <p v-if="group.projectName" class="heading mb-0">{{ group.projectName }}</p>
              <p class="subtitle is-7 mb-0">{{ group.deliverableName }}</p>
            </div>
          </div>
          <label
            v-if="selectable"
            class="group-checkbox"
            @click.stop
          >
            <input
              type="checkbox"
              :checked="isGroupFullySelected(group)"
              :indeterminate="isGroupPartiallySelected(group)"
              @change="$emit('toggle-select-group', group.items.map(g => g.item.id))"
            />
          </label>
          <span v-else class="group-count">{{ group.items.length }}</span>
        </div>

        <!-- Group items -->
        <template v-if="!collapsedGroups[group.reviewId]">
          <div
            v-for="{ item, flatIndex } in group.items"
            :key="item.id"
            class="thumb-card"
            :class="{
              'is-stacked': reviewTags,
              'is-active': flatIndex === activeIndex,
              'is-approved': item.status === REVIEW_ITEM_STATUS.APPROVED,
              'is-selected': selectable && selectedIds.includes(item.id),
              'is-indented': group.deliverableName
            }"
            @click="$emit('select', flatIndex)"
          >
            <label
              v-if="selectable"
              class="thumb-checkbox"
              @click.stop
            >
              <input
                type="checkbox"
                :checked="selectedIds.includes(item.id)"
                @change="$emit('toggle-select', item.id)"
              />
            </label>
            <div class="thumb-preview">
              <img
                v-if="item.type === 'image'"
                class="thumb-img"
                :src="item.url"
                :alt="item.name"
              />
              <img
                v-else-if="item.poster"
                class="thumb-img"
                :src="item.poster"
                :alt="item.name"
              />
              <div v-else class="thumb-placeholder" :style="{ background: item.thumbnailColor || defaultGradient(item.type) }">
                <span class="icon thumb-icon">
                  <font-awesome-icon :icon="['fal', item.type === 'video' ? 'film' : 'image']" />
                </span>
              </div>
              <span v-if="item.type === 'video' && item.duration" class="duration-badge">
                {{ formatDuration(item.duration) }}
              </span>
            </div>
            <div v-if="$slots['card-menu']" class="thumb-menu" @click.stop>
              <button
                class="thumb-menu-btn"
                :class="{ 'is-open': openMenuId === item.id }"
                aria-label="Asset actions"
                title="Asset actions"
                @click="toggleMenu(item.id, $event)"
              >
                <font-awesome-icon :icon="['fas', 'ellipsis']" />
              </button>
              <!-- Teleported: the scrolling rail would clip it otherwise -->
              <Teleport to="body">
                <div
                  v-if="openMenuId === item.id"
                  class="thumb-menu-list"
                  :style="menuPosition"
                  @click.stop="openMenuId = null"
                >
                  <slot name="card-menu" :item="item" :index="flatIndex"></slot>
                </div>
              </Teleport>
            </div>
            <button
              v-if="removable"
              class="thumb-remove-btn"
              title="Remove item"
              @click.stop="$emit('remove', flatIndex)"
            >
              <font-awesome-icon :icon="['fal', 'trash-can']" />
            </button>
            <div class="thumb-info">
              <p class="thumb-name" :title="item.name">{{ item.name }}</p>
              <template v-if="layout === 'vertical' && reviewTags">
                <p class="thumb-meta">
                  <span>v{{ item.versionCount || 1 }}<template v-if="item.currentVersionPromoted"> · Client v{{ item.currentClientVersionNumber }}</template></span>
                  <span v-if="item.commentCount" class="thumb-meta-comments">
                    <font-awesome-icon :icon="['fal', 'comment']" class="mr-1" />{{ item.commentCount }}
                  </span>
                </p>
                <div class="thumb-review-tags">
                  <span class="thumb-review-tag">
                    <span class="tag-label">Internal</span>
                    <span class="tag is-small" :class="reviewTagClass(item.internalStatus)">{{ reviewTagLabel(item.internalStatus) }}</span>
                  </span>
                  <span class="thumb-review-tag">
                    <span class="tag-label">Client</span>
                    <span v-if="!item.clientVersionCount" class="tag is-small is-not-sent">Not sent</span>
                    <span v-else class="tag is-small" :class="reviewTagClass(item.clientStatus)">{{ reviewTagLabel(item.clientStatus) }}</span>
                  </span>
                </div>
              </template>
              <template v-else-if="layout === 'vertical'">
                <span v-if="item.type === 'video' && item.duration" class="thumb-duration">
                  {{ formatDuration(item.duration) }}
                </span>
                <div v-if="item.versionCount" class="thumb-versions">
                  <span>v{{ item.versionCount }}</span>
                  <template v-if="item.currentVersionPromoted">
                    <span class="thumb-version-divider">·</span>
                    <span>Client v{{ item.currentClientVersionNumber }}</span>
                  </template>
                  <template v-else-if="item.hasPromotedHistory">
                    <span class="thumb-version-divider">·</span>
                    <span class="thumb-not-promoted">Not visible to client</span>
                  </template>
                  <template v-else-if="item.clientVersionCount">
                    <span class="thumb-version-divider">·</span>
                    <span>Client v{{ item.clientVersionCount }}</span>
                  </template>
                </div>
                <div class="thumb-bottom-row">
                  <span v-if="item.status && item.status !== REVIEW_ITEM_STATUS.PENDING_REVIEW" class="thumb-status" :class="statusClass(item.status)">
                    <font-awesome-icon :icon="['fal', statusIcon(item.status)]" class="mr-1" />
                    {{ statusLabel(item.status) }}
                  </span>
                  <span v-if="item.commentCount" class="has-text-grey thumb-comments">
                    <font-awesome-icon :icon="['fal', 'comment']" class="mr-1" />
                    {{ item.commentCount }}
                  </span>
                </div>
              </template>
              <template v-else>
                <div v-if="item.versionCount" class="thumb-versions">
                  <span>v{{ item.versionCount }}</span>
                  <template v-if="item.currentVersionPromoted">
                    <span class="thumb-version-divider">·</span>
                    <span>Client v{{ item.currentClientVersionNumber }}</span>
                  </template>
                  <template v-else-if="item.hasPromotedHistory">
                    <span class="thumb-version-divider">·</span>
                    <span class="thumb-not-promoted">Not visible to client</span>
                  </template>
                  <template v-else-if="item.clientVersionCount">
                    <span class="thumb-version-divider">·</span>
                    <span>Client v{{ item.clientVersionCount }}</span>
                  </template>
                </div>
                <div class="is-flex is-align-items-center gap-1">
                  <span v-if="item.status && item.status !== REVIEW_ITEM_STATUS.PENDING_REVIEW" class="thumb-status" :class="statusClass(item.status)">
                    <font-awesome-icon :icon="['fal', statusIcon(item.status)]" class="mr-1" />
                    {{ statusLabel(item.status) }}
                  </span>
                  <span v-if="item.commentCount" class="has-text-grey thumb-comments">
                    <font-awesome-icon :icon="['fal', 'comment']" class="mr-1" />
                    {{ item.commentCount }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { REVIEW_ITEM_STATUS, formatTimestamp as formatDuration } from '@/utils/reviewUtils'

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  layout: {
    type: String,
    default: 'horizontal',
  },
  removable: {
    type: Boolean,
    default: false,
  },
  // Admin rail: big thumbnail on top + Internal/Client tags (same as episode slots).
  reviewTags: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
})

defineEmits(['select', 'remove', 'toggle-select', 'toggle-select-group'])

// ─── Card ⋯ menu ────────────────────────────────────────
const MENU_WIDTH = 200
const openMenuId = ref(null)
const menuPosition = ref({})
function toggleMenu(itemId, event) {
  if (openMenuId.value === itemId) {
    openMenuId.value = null
    return
  }
  const rect = event.currentTarget.getBoundingClientRect()
  menuPosition.value = {
    top: `${rect.bottom + 4}px`,
    left: `${Math.max(8, rect.right - MENU_WIDTH)}px`,
  }
  openMenuId.value = itemId
}
const closeMenu = () => { openMenuId.value = null }
onMounted(() => {
  document.addEventListener('click', closeMenu)
  // Fixed-position menu would drift from its card on scroll
  window.addEventListener('scroll', closeMenu, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', closeMenu)
  window.removeEventListener('scroll', closeMenu, true)
})

// Mirrors EpisodeReviewSlots' Internal/Client tags so both rails read the same
function reviewTagLabel(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'Approved'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'Changes'
  if (status === REVIEW_ITEM_STATUS.IN_REVISION) return 'In revision'
  if (status === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return 'Finalizing'
  return 'Pending'
}
function reviewTagClass(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'is-success'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'is-warning'
  return 'is-info'
}

// ─── Group logic ────────────────────────────────────────
const collapsedGroups = ref({})

const groups = computed(() => {
  const result = []
  let current = null

  props.items.forEach((item, index) => {
    const reviewId = item._reviewId || '__ungrouped__'
    if (!current || current.reviewId !== reviewId) {
      current = {
        reviewId,
        deliverableName: item._deliverableName || '',
        // Episodes are labelled by their public title; plain projects by project name.
        projectName: item._episodeTitle || item._projectName || '',
        items: [],
      }
      result.push(current)
    }
    current.items.push({ item, flatIndex: index })
  })

  return result
})

function isGroupFullySelected(group) {
  return group.items.every(g => props.selectedIds.includes(g.item.id))
}

function isGroupPartiallySelected(group) {
  const selected = group.items.filter(g => props.selectedIds.includes(g.item.id))
  return selected.length > 0 && selected.length < group.items.length
}

function toggleGroup(reviewId) {
  collapsedGroups.value = {
    ...collapsedGroups.value,
    [reviewId]: !collapsedGroups.value[reviewId],
  }
}

// ─── Helpers ────────────────────────────────────────────
function defaultGradient(type) {
  return type === 'video'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
}

function statusClass(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'is-approved'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'is-changes'
  if (status === REVIEW_ITEM_STATUS.IN_REVISION) return 'is-revision'
  if (status === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return 'is-finalization'
  return ''
}

function statusIcon(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'check'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'pen-to-square'
  if (status === REVIEW_ITEM_STATUS.IN_REVISION) return 'pen-ruler'
  if (status === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return 'wand-magic-sparkles'
  return 'clock'
}

function statusLabel(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'Approved'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'Changes'
  if (status === REVIEW_ITEM_STATUS.IN_REVISION) return 'Revising'
  if (status === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return 'Finalization'
  return ''
}
</script>

<style lang="scss" scoped>
.review-thumbnail-strip {
  flex-shrink: 0;
  border-top: 1px solid var(--bulma-border, #eee);
  padding: 0.5rem 0;
}

.strip-scroll {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.25rem 0.5rem;
  scroll-behavior: smooth;

  // Hide scrollbar but keep scrollable
  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--bulma-grey-lighter, #ddd);
    border-radius: 2px;
  }
}

.thumb-checkbox {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--bulma-primary, #485fc7);
  }
}

.thumb-card.is-selected {
  border-color: var(--bulma-primary, #485fc7);
  background: rgba(72, 95, 199, 0.08);
}

.thumb-remove-btn {
  display: none;
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  border: none;
  background: none;
  color: var(--bulma-danger, #f14668);
  font-size: 0.75rem;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    color: var(--bulma-danger-dark, #cc0f35);
  }
}

.thumb-menu {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 3;
}

.thumb-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--bulma-text, #4a4a4a);
  font-size: 0.8rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;

  &.is-open,
  &:focus-visible {
    opacity: 1;
  }
}

.thumb-menu-list {
  position: fixed;
  z-index: 10000;
  width: 200px;
  box-sizing: border-box;
  padding: 0.3rem;
  background: var(--bulma-scheme-main, #fff);
  border: 1px solid var(--bulma-border, #dbdbdb);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(10, 10, 10, 0.12);
  display: flex;
  flex-direction: column;
}

:slotted(.thumb-menu-item) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.45rem 0.6rem;
  border: none;
  border-radius: 4px;
  background: none;
  text-align: left;
  font: inherit;
  font-size: 0.8rem;
  color: var(--bulma-text-strong, #363636);
  cursor: pointer;

  &:hover {
    background: var(--bulma-scheme-main-bis, #f5f5f5);
  }

  &.is-danger {
    color: var(--bulma-danger, #f14668);
  }

  small {
    font-size: 0.7rem;
    color: var(--bulma-grey, #7a7a7a);
  }
}

:slotted(.thumb-menu-divider) {
  height: 1px;
  margin: 0.25rem 0;
  background: var(--bulma-border-weak, #ededed);
}

.thumb-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-size: 0.72rem;
  color: var(--bulma-text-weak, #6b7280);
}

.thumb-review-tags {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.35rem;
}

.thumb-review-tag {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;

  .tag-label {
    font-size: 0.58rem;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: hsl(0, 0%, 65%);
  }

  .tag.is-not-sent {
    background: hsl(0, 0%, 94%);
    color: hsl(0, 0%, 55%);
  }
}

.thumb-card {
  flex: 0 0 150px;
  cursor: pointer;
  border-radius: 0.5rem;
  border: 2px solid transparent;
  overflow: hidden;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: var(--bulma-scheme-main, #fff);

  &:hover {
    border-color: var(--bulma-grey-lighter, #ddd);

    .thumb-menu-btn {
      opacity: 1;
    }

    .thumb-remove-btn {
      display: flex;
    }
  }

  &.is-approved {
    border-color: var(--bulma-success, #48c78e);
    background: hsla(var(--bulma-success-h), var(--bulma-success-s), var(--bulma-success-l), 0.06);
  }

  &.is-active {
    border-color: var(--bulma-primary, #485fc7);
    box-shadow: 0 0 0 1px var(--bulma-primary, #485fc7);
  }
}

.thumb-preview {
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: #000;

  .thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .thumb-icon {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
  }
}

.duration-badge {
  position: absolute;
  bottom: 3px;
  right: 3px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 0.7rem;
  padding: 1px 5px;
  border-radius: 2px;
  font-variant-numeric: tabular-nums;
}

.thumb-info {
  padding: 0.35rem 0.5rem;
}

.thumb-name {
  font-size: 0.8rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.thumb-versions {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.7rem;
  color: var(--bulma-grey, #7a7a7a);
}

.thumb-version-divider {
  margin: 0 0.1rem;
}

.thumb-not-promoted {
  color: var(--bulma-warning-dark, #c08400);
  font-style: italic;
}

.thumb-status {
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 3px;
  padding: 1px 4px;
  white-space: nowrap;

  &.is-approved {
    color: var(--bulma-success, #48c78e);
  }

  &.is-changes {
    color: var(--bulma-danger, #f14668);
  }

  &.is-revision {
    color: hsl(34, 100%, 56%);
  }

  &.is-finalization {
    color: var(--bulma-warning, hsl(44, 100%, 50%));
  }
}

.thumb-comments {
  font-size: 0.75rem;
}

.gap-1 {
  gap: 0.25rem;
}

// ─── Group header ───────────────────────────────────────
.thumb-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.6rem;
  cursor: pointer;
  border-radius: 0.4rem;
  user-select: none;
  border: 1px solid hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l));
  background: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.08);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:not(:first-child) {
    margin-top: 0.35rem;
  }

  &:hover {
    background: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.14);
  }
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.group-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--bulma-primary, #485fc7);
  }
}

.group-chevron {
  color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l));
  font-size: 0.7rem;
  flex-shrink: 0;
}

.group-label {
  min-width: 0;

  .heading,
  .subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.group-count {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l));
  background: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.1);
  border: 1px solid hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l));
  border-radius: 10px;
  padding: 0 0.45rem;
  line-height: 1.6;
  min-width: 1.4rem;
  text-align: center;
}

// ─── Vertical layout for sidebar placement ──────────────
.review-thumbnail-strip.is-vertical {
  border-top: none;
  padding: 0;
  flex: 1;
  overflow: hidden;

  .strip-scroll {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0.5rem;
    gap: 0.35rem;

    &::-webkit-scrollbar {
      width: 4px;
      height: auto;
    }
  }

  .thumb-card {
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    position: relative;
    border: 1px solid var(--bulma-border, #ddd);
    background: var(--bulma-scheme-main-bis, #fafafa);
    border-radius: 0.5rem;
    padding: 0.4rem;

    &.is-indented {
      margin-left: 0.75rem;
    }

    &:hover {
      border-color: var(--bulma-grey-light, #b5b5b5);
      background: var(--bulma-scheme-main-ter, #f0f0f0);
    }

    &.is-approved {
      border-color: var(--bulma-success, #48c78e);
      background: hsla(var(--bulma-success-h), var(--bulma-success-s), var(--bulma-success-l), 0.06);
    }

    &.is-active {
      border-color: var(--bulma-primary, #485fc7);
      background: rgba(72, 95, 199, 0.06);
      box-shadow: 0 0 0 1px var(--bulma-primary, #485fc7);
    }
  }

  .thumb-preview {
    aspect-ratio: 1 / 1;
    width: 64px;
    min-width: 64px;
    flex-shrink: 0;
    border-radius: 0.35rem;
  }

  .thumb-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 0.15rem 0.5rem;
  }

  .thumb-name {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .thumb-duration {
    font-size: 0.7rem;
    color: var(--bulma-grey, #7a7a7a);
    font-variant-numeric: tabular-nums;
  }

  .thumb-bottom-row {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .thumb-checkbox {
    top: 50%;
    left: auto;
    right: 12px;
    transform: translateY(-50%);
  }

  // Review-tags card: full-width thumbnail on top, details underneath
  .thumb-card.is-stacked {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;

    .thumb-preview {
      width: 100%;
      min-width: 0;
      aspect-ratio: 16 / 9;

      // Letterbox, don't crop: portrait cuts should read as portrait
      .thumb-img {
        object-fit: contain;
      }
    }

    .thumb-info {
      padding: 0 0.15rem 0.15rem;
      gap: 0.2rem;
    }

    .thumb-name {
      font-weight: 600;
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .thumb-card.is-selected {
    border-color: var(--bulma-primary, #485fc7);
    background: rgba(72, 95, 199, 0.08);
  }

  // Hide the overlay duration badge in vertical mode (shown inline instead)
  .duration-badge {
    display: none;
  }
}
</style>
